import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDateTime } from '@/lib/dateFormat';
import type { Contract } from '@/services/contractService';

interface AuditEntry {
  label: string;
  name?: string | null;
  email?: string | null;
  at?: string | null;
}

/** Name when it could be resolved, otherwise the stored email; hovering reveals the email. */
function Person({ name, email }: { name?: string | null; email?: string | null }) {
  const display = name || email;
  if (!display) return <span className="text-muted-foreground">Unknown</span>;
  if (!email || display === email) return <span className="font-medium">{display}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="font-medium cursor-default">{display}</span>
      </TooltipTrigger>
      <TooltipContent>{email}</TooltipContent>
    </Tooltip>
  );
}

export function AuditTrail({ contract, inline = false }: { contract: Contract; inline?: boolean }) {
  const entries: AuditEntry[] = [
    { label: 'Created', name: contract.createdByName, email: contract.createdBy, at: contract.createdAt },
  ];

  // Only show "Last updated" when someone actually changed the contract after it was created.
  if (contract.updatedBy) {
    entries.push({ label: 'Last updated', name: contract.updatedByName, email: contract.updatedBy, at: contract.updatedAt });
  }
  if (contract.isDeleted) {
    entries.push({ label: 'Deleted', name: contract.deletedByName, email: contract.deletedBy, at: contract.deletedAt });
  }

  if (inline) {
    // Compact single line for page headers: "Created by Anish · 20 Nov 2025, 17:13"
    return (
      <dl className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-xs text-muted-foreground">
        {entries.map((entry) => (
          <div key={entry.label} className="flex items-baseline gap-1.5">
            <dt>{entry.label} by</dt>
            <dd className="flex items-baseline gap-1.5">
              <span className="text-foreground">
                <Person name={entry.name} email={entry.email} />
              </span>
              {entry.at && <span className="tabular-nums">· {formatDateTime(entry.at)}</span>}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className="space-y-3 text-sm">
      {entries.map((entry) => (
        <div key={entry.label}>
          <dt className="text-xs text-muted-foreground">{entry.label}</dt>
          <dd className="mt-0.5">
            <Person name={entry.name} email={entry.email} />
            {entry.at && <span className="block text-xs text-muted-foreground tabular-nums">{formatDateTime(entry.at)}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}
